// InvestorsPage.jsx
// ✅ Premium Investors page (React + MUI)
// ✅ FIXED: removed all borders + prevented overlap/overflow

import * as React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Stack,
  Chip,
 
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Avatar,
} from "@mui/material";
import { alpha, useTheme, keyframes } from "@mui/material/styles";

import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";

import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BreadcrumbNav from "./BreadcrumbNav";

const COMPANY = "Sudhanshu & Siddharth Innovations Pvt. Ltd.";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1582719478185-2b3b01e7a8b3?auto=format&fit=crop&w=1600&q=70";

const INVESTOR_IMAGE =
  "https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1600&q=70";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const floaty = keyframes`
  0%   { transform: translateY(0px); }
  50%  { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

const METRICS = [
  { k: "Pilot readiness", v: "Protocols + deployment planning" },
  { k: "Hospital pathway", v: "Institutional collaboration" },
  { k: "Engineering maturity", v: "Requirements → prototypes" },
  { k: "IP discipline", v: "Confidentiality + filing strategy" },
];

function Section({ title, subtitle, icon, children }) {
  const theme = useTheme();

  return (
    <Box component="section" sx={{ py: { xs: 5, md: 8 } }}>
      <Container maxWidth="xl">
        <Stack spacing={1.25} sx={{ mb: { xs: 2.5, md: 3 } }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            {icon ? (
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 3,
                  display: "grid",
                  placeItems: "center",
                  background: alpha(theme.palette.primary.main, 0.08),
                }}
              >
                {React.cloneElement(icon, {
                  sx: { color: theme.palette.primary.main },
                })}
              </Box>
            ) : null}

            <Typography
              sx={{
                fontWeight: 900,
                letterSpacing: -0.4,
                fontSize: { xs: 22, md: 30 },
              }}
            >
              {title}
            </Typography>
          </Stack>

          {subtitle ? (
            <Typography
              sx={{
                color: alpha("#0b1220", 0.72),
                lineHeight: 1.8,
                maxWidth: 980,
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Stack>

        {children}
      </Container>
    </Box>
  );
}

function PremiumCard({ children, delay = 0 }) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 5,
        p: { xs: 2.2, sm: 2.6 },
        background: "#fff",
        // ✅ removed border completely
        border: "none",
        boxShadow: `0 14px 40px ${alpha("#0b1220", 0.10)}`,
        overflow: "hidden",
        position: "relative",
        animation: `${fadeUp} 650ms ease both`,
        animationDelay: `${delay}ms`,
        transition: "transform 220ms ease, box-shadow 220ms ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: `0 18px 55px ${alpha("#0b1220", 0.16)}`,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `radial-gradient(800px 220px at 10% 0%, ${alpha(
            theme.palette.primary.main,
            0.10
          )} 0%, transparent 60%)`,
          pointerEvents: "none",
        },
      }}
    >
      <Box sx={{ position: "relative" }}>{children}</Box>
    </Paper>
  );
}

function StatPill({ label, value }) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 3,
        background: alpha(theme.palette.primary.main, 0.06),
        // ✅ no border
        border: "none",
        height: "100%",
      }}
    >
      <Typography
        sx={{ fontSize: 12, color: alpha("#0b1220", 0.62), fontWeight: 800 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.4,
          fontWeight: 900,
          lineHeight: 1.25,
          color: "#0b1220",
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
}

export default function InvestorsPage() {
  const theme = useTheme();

  return (
    <>
    {/* <BreadcrumbNav/> */}
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F6F8FC",
        color: "#0b1220",
        // ✅ IMPORTANT: prevents “overlap” caused by horizontal overflow
        overflowX: "hidden",
      }}
    >
      {/* HERO */}
      <Box
        component="header"
        sx={{
          position: "relative",
          overflow: "hidden",
          pt: { xs: 7, md: 10 },
          pb: { xs: 6, md: 8 },
          bgcolor: "#fff",
          // ✅ removed border bottom
          borderBottom: "none",
        }}
      >
        {/* Background image */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: `linear-gradient(90deg, ${alpha(
              "#fff",
              0.96
            )} 0%, ${alpha("#fff", 0.88)} 45%, ${alpha(
              "#fff",
              0.40
            )} 100%), url(${HERO_IMAGE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "saturate(1.05)",
            transform: "scale(1.03)",
          }}
        />

        {/* Decorative blob */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            right: { xs: -140, md: -120 },
            top: { xs: -120, md: -150 },
            width: { xs: 360, md: 520 },
            height: { xs: 360, md: 520 },
            borderRadius: "50%",
            background: `radial-gradient(circle at 30% 30%, ${alpha(
              theme.palette.primary.main,
              0.22
            )} 0%, transparent 60%)`,
            pointerEvents: "none",
            zIndex: 0, // ✅ behind content
          }}
        />

        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={2.2} sx={{ animation: `${fadeUp} 700ms ease both` }}>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  alignItems="center"
                >
                  <Chip
                    label="Investors"
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.10),
                      fontWeight: 900,
                    }}
                  />
                  <Chip
                    label="MedTech • Hospital-ready"
                    sx={{
                      bgcolor: alpha("#0b1220", 0.05),
                      fontWeight: 800,
                    }}
                  />
                  <Chip
                    icon={<VerifiedOutlinedIcon />}
                    label="Validation-first"
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.08),
                      fontWeight: 800,
                      "& .MuiChip-icon": { color: theme.palette.success.main },
                    }}
                  />
                </Stack>

                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 950,
                    letterSpacing: -0.8,
                    fontSize: { xs: 30, sm: 40, md: 52 },
                    lineHeight: 1.08,
                  }}
                >
                  Clinical technology that improves outcomes, reduces workload,
                  and scales across hospitals.
                </Typography>

                <Typography
                  sx={{
                    color: alpha("#0b1220", 0.72),
                    lineHeight: 1.85,
                    maxWidth: 760,
                    wordBreak: "break-word",
                  }}
                >
                  <b>{COMPANY}</b> is building a workflow-first MedTech platform
                  that unifies device signals and care actions into one reliable,
                  traceable system—so teams can act faster with confidence and
                  operational clarity.
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      borderRadius: 3,
                      py: 1.2,
                      textTransform: "none",
                      fontWeight: 900,
                      boxShadow: `0 14px 32px ${alpha(
                        theme.palette.primary.main,
                        0.22
                      )}`,
                    }}
                  >
                    Request Investor Deck
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<DownloadOutlinedIcon />}
                    sx={{
                      borderRadius: 3,
                      py: 1.2,
                      textTransform: "none",
                      fontWeight: 900,
                      // ✅ remove outline border visually (still looks outlined via bg)
                      borderColor: "transparent",
                      bgcolor: alpha("#0b1220", 0.04),
                      "&:hover": {
                        borderColor: "transparent",
                        bgcolor: alpha("#0b1220", 0.07),
                      },
                    }}
                  >
                    Download One-Pager
                  </Button>

                  
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 5,
                  overflow: "hidden",
                  // ✅ remove border
                  border: "none",
                  boxShadow: `0 18px 55px ${alpha("#0b1220", 0.12)}`,
                  animation: `${fadeUp} 750ms ease both`,
                  background: "#fff",
                }}
              >
                {/* Card image */}
                <Box
                  sx={{
                    height: 170,
                    backgroundImage: `linear-gradient(180deg, ${alpha(
                      "#0b1220",
                      0.15
                    )} 0%, ${alpha("#0b1220", 0.55)} 100%), url(${INVESTOR_IMAGE})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    position: "relative",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      position: "absolute",
                      left: 16,
                      bottom: 14,
                      color: "#fff",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: alpha(theme.palette.primary.main, 0.9),
                      }}
                    >
                      <HubOutlinedIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                        Quick Snapshot
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: alpha("#fff", 0.82) }}
                      >
                        Readiness • Validation • Deployment
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Content */}
                <Box sx={{ p: { xs: 2.2, sm: 2.6 } }}>
                  <Grid container spacing={1.3}>
                    {METRICS.map((m) => (
                      <Grid item xs={12} sm={6} key={m.k}>
                        <StatPill label={m.k} value={m.v} />
                      </Grid>
                    ))}
                  </Grid>

                  {/* ✅ Divider without border line (just spacing) */}
                  <Box sx={{ my: 2 }} />

                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <Box
                      sx={{
                        mt: 0.2,
                        width: 34,
                        height: 34,
                        borderRadius: 3,
                        display: "grid",
                        placeItems: "center",
                        background: alpha(theme.palette.primary.main, 0.08),
                        animation: `${floaty} 3.6s ease-in-out infinite`,
                      }}
                    >
                      <TimelineOutlinedIcon
                        sx={{ color: theme.palette.primary.main }}
                        fontSize="small"
                      />
                    </Box>
                    <Typography
                      sx={{ color: alpha("#0b1220", 0.72), lineHeight: 1.8 }}
                    >
                      Focused on measurable hospital outcomes: reduced manual
                      documentation, stronger traceability, and faster escalation
                      when deterioration risk increases.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* WHY NOW + WHO IT SERVES */}
      <Section
        title="Why Now"
        icon={<TimelineOutlinedIcon />}
        subtitle="Hospitals need fewer tools, clearer escalation, and audit-ready traceability—without adding burden to already stretched clinical teams."
      >
        <Grid container spacing={{ xs: 2, md: 2.5 }}>
          <Grid item xs={12} md={6}>
            <PremiumCard>
              <Stack spacing={1.4}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocalHospitalOutlinedIcon
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Typography sx={{ fontWeight: 950, fontSize: 18 }}>
                    Drivers
                  </Typography>
                </Stack>

                {/* ✅ Divider removed (spacing only) */}
                <Box />

                <List sx={{ py: 0 }}>
                  {[
                    "Pressure to improve safety + efficiency with fewer resources",
                    "Time lost to manual documentation and disconnected device systems",
                    "Delayed insight for early deterioration detection",
                    "Procurement shifting toward traceability + audit-ready reporting",
                  ].map((x) => (
                    <ListItem key={x} sx={{ px: 0, py: 0.6 }}>
                      <ListItemIcon sx={{ minWidth: 34 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: 99,
                            bgcolor: alpha(theme.palette.primary.main, 0.75),
                            mt: 0.7,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={x}
                        primaryTypographyProps={{
                          sx: {
                            color: alpha("#0b1220", 0.78),
                            lineHeight: 1.65,
                            fontWeight: 650,
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Stack>
            </PremiumCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <PremiumCard delay={60}>
              <Stack spacing={1.4}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <GroupsOutlinedIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography sx={{ fontWeight: 950, fontSize: 18 }}>
                    Who It Serves
                  </Typography>
                </Stack>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.1}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<MailOutlineIcon />}
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 900,
                      // ✅ remove border completely
                      borderColor: "transparent",
                      bgcolor: alpha("#0b1220", 0.04),
                      "&:hover": {
                        borderColor: "transparent",
                        bgcolor: alpha("#0b1220", 0.07),
                      },
                    }}
                  >
                    Investor Relations
                  </Button>

                  <Button
                    fullWidth
                    variant="contained"
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 900,
                    }}
                  >
                    Schedule a Call
                  </Button>
                </Stack>

                {/* ✅ optional mini note */}
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1.4,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                  }}
                >
                  <Typography sx={{ fontWeight: 850, fontSize: 13 }}>
                    We share deck, one-pager, and pilot plan on request.
                  </Typography>
                  <Typography sx={{ color: alpha("#0b1220", 0.7), fontSize: 13, mt: 0.4 }}>
                    Confidential materials are provided under NDA where needed.
                  </Typography>
                </Box>
              </Stack>
            </PremiumCard>
          </Grid>
        </Grid>
      </Section>

      {/* FOOTER SPACER */}
      <Box sx={{ py: 4 }} />
    </Box>
    </>
  );
}
